import React, { useRef, useState, useCallback } from "react";
import { DECISION_META, ORDER } from "./decisionMeta";

// Swipe → decision mapping.
//   →  BUY    (positive horizontal)
//   ←  SELL   (negative horizontal)
//   ↑  KEEP   (negative vertical)
//   ↓  AVOID  (positive vertical)
const SWIPE_THRESHOLD = 90; // px
const SWIPE_LOCK_RATIO = 1.2; // dominant axis must exceed other by this factor

function directionFor(dx, dy) {
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    if (ax < SWIPE_THRESHOLD && ay < SWIPE_THRESHOLD) return null;
    if (ax > ay * SWIPE_LOCK_RATIO) return dx > 0 ? "BUY" : "SELL";
    if (ay > ax * SWIPE_LOCK_RATIO) return dy > 0 ? "AVOID" : "KEEP";
    return null;
}

function previewFor(dx, dy) {
    // Lower threshold for "intent" preview so user sees overlay before commit.
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    if (ax < 30 && ay < 30) return null;
    if (ax > ay) return dx > 0 ? "BUY" : "SELL";
    return dy > 0 ? "AVOID" : "KEEP";
}

// --- Haptic + sound feedback ------------------------------------------------
let _audioCtx = null;
function playTickSound(frequency = 880) {
    try {
        if (!_audioCtx) {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return;
            _audioCtx = new AC();
        }
        const ctx = _audioCtx;
        if (ctx.state === "suspended") ctx.resume();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(frequency, ctx.currentTime);
        gain.gain.setValueAtTime(0.0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.13);
    } catch (_) { /* silent */ }
}

function triggerCommitFeedback(decision) {
    // Haptic on supporting browsers (mobile Chrome/Firefox).
    if (navigator.vibrate) {
        try { navigator.vibrate(decision === "BUY" || decision === "SELL" ? [20] : [10, 30, 10]); } catch (_) {}
    }
    // Subtle distinct pitch per decision so user hears the call.
    const pitches = { BUY: 1046, KEEP: 880, AVOID: 587, SELL: 440 };
    playTickSound(pitches[decision] || 700);
}

function Stat({ label, value }) {
    return (
        <div className="border border-white/10 bg-black/40 px-2 py-1.5 text-center">
            <div className="text-white/40 uppercase tracking-widest">{label}</div>
            <div className="font-display font-black text-lg text-white">{value}</div>
        </div>
    );
}

function ConfidenceSlider({ value, onChange, accentColor }) {
    const label = value >= 80 ? "Going all-in" :
                  value >= 60 ? "Confident" :
                  value >= 40 ? "Even-money" :
                  value >= 20 ? "Hedging" : "Long shot";
    const multiplier = (0.5 + value / 100).toFixed(2);
    return (
        <div className="space-y-1.5" data-testid="confidence-slider-row" data-no-swipe>
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest">
                <span className="text-white/60">Confidence</span>
                <span style={{ color: accentColor }}>{label} · {value}% · {multiplier}× stakes</span>
            </div>
            <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full accent-fa-yellow cursor-pointer"
                style={{ accentColor }}
                data-testid="confidence-slider"
            />
            <div className="flex justify-between font-mono text-[9px] text-white/30">
                <span>0% · half-stakes (0.5×)</span>
                <span>50% · default (1.0×)</span>
                <span>100% · max (1.5×)</span>
            </div>
        </div>
    );
}

export default function CardModeGame({
    player, index, total, pick, onPick, onBack, onSkipToReview, canGoBack,
    confidence = 50, onConfidenceChange,
}) {
    const [drag, setDrag] = useState({ dx: 0, dy: 0, active: false });
    const startRef = useRef(null);
    const pointerIdRef = useRef(null);
    const cardRef = useRef(null);

    const tagsLine = (player.tags || []).slice(0, 4).join(" · ");
    const ctx = player.crowdContext || "NEUTRAL";
    const ctxLabel = ctx === "BUY_INS" ? "Mass-buy this GW" :
                     ctx === "SELL_OFFS" ? "Mass-sell this GW" :
                     "Neutral flow";
    const ctxColor = ctx === "BUY_INS" ? "#9EE8B4" :
                     ctx === "SELL_OFFS" ? "#F5A78D" : "#666";
    const netTr = player.netTransfersEvent || 0;
    const netSign = netTr > 0 ? "+" : "";

    const commit = useCallback((decision) => {
        triggerCommitFeedback(decision);
        // animate card off-screen briefly, then call onPick (parent advances).
        const dir = decision === "BUY" ? { x: 600, y: 0 }
                  : decision === "SELL" ? { x: -600, y: 0 }
                  : decision === "KEEP" ? { x: 0, y: -600 }
                  : { x: 0, y: 600 };
        setDrag({ dx: dir.x, dy: dir.y, active: false });
        setTimeout(() => {
            setDrag({ dx: 0, dy: 0, active: false });
            onPick(decision);
        }, 180);
    }, [onPick]);

    const onPointerDown = (e) => {
        // Ignore drags starting on the action buttons / nav links.
        if (e.target.closest("[data-no-swipe]")) return;
        startRef.current = { x: e.clientX, y: e.clientY };
        pointerIdRef.current = e.pointerId;
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
        setDrag({ dx: 0, dy: 0, active: true });
    };
    const onPointerMove = (e) => {
        if (!startRef.current || pointerIdRef.current !== e.pointerId) return;
        const dx = e.clientX - startRef.current.x;
        const dy = e.clientY - startRef.current.y;
        setDrag({ dx, dy, active: true });
    };
    const onPointerUp = (e) => {
        if (!startRef.current || pointerIdRef.current !== e.pointerId) return;
        const dx = e.clientX - startRef.current.x;
        const dy = e.clientY - startRef.current.y;
        startRef.current = null;
        pointerIdRef.current = null;
        const decision = directionFor(dx, dy);
        if (decision) {
            commit(decision);
        } else {
            setDrag({ dx: 0, dy: 0, active: false });
        }
    };

    const preview = drag.active ? previewFor(drag.dx, drag.dy) : null;
    const rotate = Math.max(-15, Math.min(15, drag.dx / 18));
    const cardStyle = {
        transform: `translate(${drag.dx}px, ${drag.dy}px) rotate(${rotate}deg)`,
        transition: drag.active ? "none" : "transform 180ms cubic-bezier(.2,.8,.2,1)",
        borderColor: ctxColor + "55",
        touchAction: "none",
    };

    return (
        <section
            ref={cardRef}
            className="relative border-2 bg-gradient-to-br from-fa-violet/10 to-[#0d0d0d] p-6 md:p-8 space-y-6 select-none"
            style={cardStyle}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            data-testid="card-mode-game"
        >
            {/* Swipe preview overlay */}
            {preview && (
                <div
                    className="pointer-events-none absolute inset-0 flex items-center justify-center font-display font-black text-6xl md:text-7xl uppercase tracking-tight"
                    style={{
                        color: DECISION_META[preview].color,
                        textShadow: "0 0 32px rgba(0,0,0,0.6)",
                        opacity: Math.min(1, Math.max(Math.abs(drag.dx), Math.abs(drag.dy)) / SWIPE_THRESHOLD),
                    }}
                    data-testid={`swipe-preview-${preview.toLowerCase()}`}
                >
                    {preview}
                </div>
            )}

            <div className="space-y-2" data-no-swipe>
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest">
                    <span className="text-fa-yellow">Card {index + 1} of {total}</span>
                    <button
                        type="button"
                        onClick={onSkipToReview}
                        className="text-white/40 hover:text-white"
                        data-testid="card-skip-review-btn"
                    >
                        See review →
                    </button>
                </div>
                <div className="h-1 bg-white/10 overflow-hidden">
                    <div className="h-full bg-fa-yellow transition-all" style={{ width: `${((index + 1) / total) * 100}%` }} />
                </div>
            </div>

            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest" style={{ color: ctxColor }} data-testid={`card-crowd-${ctx.toLowerCase()}`}>
                <span>{ctxLabel}</span>
                {netTr !== 0 && (
                    <span className="text-white/40 normal-case tracking-normal">
                        {netSign}{netTr.toLocaleString()} transfers
                    </span>
                )}
            </div>

            <div>
                <div className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight" data-testid="card-player-name">
                    {player.webName}
                </div>
                <div className="font-mono text-sm text-white/60 mt-2">
                    {player.display?.subtitle}
                </div>
            </div>

            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 font-mono text-[10px]">
                <Stat label="Form" value={player.form ?? "-"} />
                <Stat label="Owned" value={`${(player.ownershipPercent ?? 0).toFixed(1)}%`} />
                <Stat label="Pts/GW" value={player.pointsPerGame ?? "-"} />
                <Stat label="Price" value={`£${(player.price ?? 0).toFixed(1)}m`} />
                <Stat label="Predicted" value={player.predictedPoints ?? "-"} />
            </div>

            {tagsLine && (
                <div className="font-mono text-[11px] text-white/70 leading-snug" data-testid="card-tags">
                    {tagsLine}
                </div>
            )}
            <div className="border-l-2 border-fa-yellow pl-3 font-mono text-[12px] text-white/80 leading-relaxed">
                {player.display?.shortReason}
            </div>

            {onConfidenceChange && (
                <ConfidenceSlider
                    value={confidence}
                    onChange={onConfidenceChange}
                    accentColor={ctxColor}
                />
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="card-decisions" data-no-swipe>
                {ORDER.map((d) => {
                    const meta = DECISION_META[d];
                    const Icon = meta.icon;
                    const active = pick === d;
                    return (
                        <button
                            key={d}
                            type="button"
                            onClick={() => commit(d)}
                            data-testid={`card-decide-${d.toLowerCase()}`}
                            className={`flex items-center justify-center gap-2 py-4 font-display font-black uppercase tracking-wide border-2 transition-all ${
                                active ? "text-black scale-105" : "text-white/70 border-white/10 hover:scale-105"
                            }`}
                            style={active
                                ? { background: meta.color, borderColor: meta.color }
                                : { borderColor: meta.color + "55" }}
                        >
                            <Icon size={16} weight="bold" /> {d}
                        </button>
                    );
                })}
            </div>

            <div className="flex items-center justify-between gap-3 pt-2 border-t border-white/5" data-no-swipe>
                <button
                    type="button"
                    onClick={onBack}
                    disabled={!canGoBack}
                    data-testid="card-back-btn"
                    className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed"
                >
                    ← Previous
                </button>
                <span className="font-mono text-[10px] text-white/30 hidden sm:inline">
                    Swipe or tap — auto-next
                </span>
                <span className="font-mono text-[10px] text-white/30 sm:hidden">
                    → BUY · ← SELL · ↑ KEEP · ↓ AVOID
                </span>
            </div>
        </section>
    );
}
